import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, CheckCircle, XCircle, Loader2, ArrowRight } from "lucide-react";
import { API } from "@/App";

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState("");
  const [language, setLanguage] = useState("es");

  // Detect browser language
  useEffect(() => {
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.includes("ca")) {
      setLanguage("ca");
    }
  }, []);

  const t = (es, ca) => language === "ca" ? ca : es;

  // Verify token on mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setVerifying(false);
        setError("no_token");
        return;
      }

      try {
        const response = await axios.post(`${API}/auth/verify-email?token=${token}`);
        setSuccess(true);
        setEmail(response.data.email);
        toast.success(t("¡Email verificado!", "Email verificat!"));
      } catch (error) {
        console.error("Verification error:", error);
        setError(error.response?.data?.detail || "verification_failed");
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
        <div className="glass rounded-3xl p-8 shadow-2xl border border-teal-400/20 text-center">
          <Loader2 className="w-12 h-12 text-teal-400 animate-spin mx-auto mb-4" />
          <p className="text-gray-400">{t("Verificando email...", "Verificant email...")}</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-red-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        </div>

        <div className="relative z-10 w-full max-w-md">
          <div className="glass rounded-3xl p-8 shadow-2xl border border-red-400/20 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-red-400 to-red-600 mb-6">
              <XCircle className="w-8 h-8 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-white mb-4">
              {error === "no_token" 
                ? t("Enlace inválido", "Enllaç invàlid")
                : t("Verificación fallida", "Verificació fallida")
              }
            </h2>
            
            <p className="text-gray-400 mb-6">
              {error === "no_token"
                ? t(
                    "No se ha proporcionado un token de verificación válido.",
                    "No s'ha proporcionat un token de verificació vàlid."
                  )
                : error.includes("expired")
                  ? t(
                      "El enlace de verificación ha expirado. Por favor, solicita uno nuevo.",
                      "L'enllaç de verificació ha caducat. Si us plau, sol·licita'n un de nou."
                    )
                  : t(
                      "No se ha podido verificar tu email. Por favor, intenta de nuevo.",
                      "No s'ha pogut verificar el teu email. Si us plau, intenta-ho de nou."
                    )
              }
            </p>

            <Link 
              to="/auth"
              className="btn-primary inline-flex items-center gap-2"
            >
              {t("Ir al inicio de sesión", "Anar a l'inici de sessió")}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950 flex items-center justify-center px-4 py-12">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-green-400/10 rounded-full blur-3xl top-1/4 left-1/4 animate-pulse"></div>
        <div className="absolute w-96 h-96 bg-teal-400/10 rounded-full blur-3xl bottom-1/4 right-1/4 animate-pulse" style={{animationDelay: '1.5s'}}></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="glass rounded-3xl p-8 shadow-2xl border border-green-400/20 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-green-600 mb-6">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          
          <h2 className="text-2xl font-bold text-white mb-4">
            {t("¡Email verificado!", "Email verificat!")}
          </h2>
          
          <p className="text-gray-400 mb-2">
            {t(
              "Tu cuenta ha sido verificada correctamente.",
              "El teu compte s'ha verificat correctament."
            )}
          </p>

          {email && (
            <p className="text-teal-400 text-sm mb-6">{email}</p>
          )}
          
          <p className="text-gray-400 mb-6">
            {t(
              "¡Ya puedes disfrutar de todas las funcionalidades de ESSENC·IA!",
              "Ja pots gaudir de totes les funcionalitats d'ESSENC·IA!"
            )}
          </p>

          <Link 
            to="/dashboard"
            className="btn-primary inline-flex items-center gap-2"
          >
            {t("Ir al Dashboard", "Anar al Dashboard")}
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
